import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconX } from "./Icons.js";

export type ToolModalProps = {
  /** Modal title, shown as a MicroLabel-style header. */
  title: string;
  /** Called when the user closes (X button, scrim tap, or ESC). */
  onClose: () => void;
  children: ReactNode;
  /**
   * "dialog" (default) centers a panel on desktop and rises a bottom sheet on
   * mobile. "wide" is the same but lets the panel grow large (for the Desmos
   * calculator, which needs room). Layout is driven by CSS + this class, so a
   * caller never forks markup per device.
   */
  size?: "dialog" | "wide";
};

// Focusable descendants for the Tab trap. Excludes tabindex="-1".
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * ToolModal — the single shared overlay shell for practice-test tools
 * (reference sheet, calculator, etc.). One component, two layouts:
 *   - desktop: centered dialog over a blurred scrim
 *   - mobile:  full-width bottom sheet that rises (same motion as RevealSheet)
 *
 * This is the design-integrity lever for Tier 1: every tool mounts inside this
 * identical chrome (header + MicroLabel title + 44px IconX close), so tools feel
 * native to the app instead of bolted on. Closes on X, scrim tap, or ESC.
 *
 * Accessibility (S5): traps Tab/Shift+Tab within the panel, restores initial
 * focus on the panel, restores the previously-focused element on close, and
 * marks the app root inert + aria-hidden while open. The overlay is portaled to
 * <body> so inerting #root can't disable the modal itself.
 */
export function ToolModal({ title, onClose, children, size = "dialog" }: ToolModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<Element | null>(null);

  useEffect(() => {
    prevFocus.current = document.activeElement;
    panelRef.current?.focus();

    // Make the rest of the app inert + hidden from assistive tech while open.
    // The overlay is portaled out of #root, so this can't reach the modal.
    const appRoot = document.getElementById("root");
    if (appRoot) {
      appRoot.setAttribute("inert", "");
      appRoot.setAttribute("aria-hidden", "true");
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled"));
      if (focusables.length === 0) {
        // Nothing focusable inside — keep focus pinned to the panel.
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === panel) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    // Lock background scroll while open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      // Restore inert BEFORE moving focus — you can't focus into an inert tree.
      if (appRoot) {
        appRoot.removeAttribute("inert");
        appRoot.removeAttribute("aria-hidden");
      }
      if (prevFocus.current instanceof HTMLElement) prevFocus.current.focus();
    };
  }, [onClose]);

  const overlay = (
    <>
      <div className="bht-tool-scrim" onClick={onClose} aria-hidden="true" />
      <div
        className={`bht-tool-modal bht-tool-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="bht-tool-modal__header">
          <span className="bht-tool-modal__title">{title}</span>
          <button
            type="button"
            className="bht-tool-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconX size={18} />
          </button>
        </div>
        <div className="bht-tool-modal__body">{children}</div>
      </div>
    </>
  );

  // Portal to <body> so the overlay lives outside the inert #root subtree.
  return typeof document !== "undefined"
    ? createPortal(overlay, document.body)
    : overlay;
}
